import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

function isPdfMedia(item) {
    const type = item?.type?.toLowerCase() || '';
    return type === 'application/pdf' || type === 'application/x-pdf' || item?.name?.toLowerCase().endsWith('.pdf');
}

const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

function MediaPreviewModal({ show, onClose, item }) {
    const [zoom, setZoom] = useState(1);
    const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
    const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
    const viewportRef = useRef(null);
    const imageRef = useRef(null);

    const isPdf = isPdfMedia(item);

    const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));

    const syncNaturalSize = (image) => {
        if (!image?.naturalWidth || !image?.naturalHeight) {
            return;
        }

        setNaturalSize({
            width: image.naturalWidth,
            height: image.naturalHeight,
        });
    };

    const measureViewport = () => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }

        const styles = window.getComputedStyle(viewport);
        const padX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
        const padY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
        setViewportSize({
            width: Math.max(viewport.clientWidth - padX, 0),
            height: Math.max(viewport.clientHeight - padY, 0),
        });
    };

    const resetPreviewState = () => {
        setZoom(1);
        setNaturalSize({ width: 0, height: 0 });
        setViewportSize({ width: 0, height: 0 });
    };

    const handleHide = () => {
        resetPreviewState();
        onClose();
    };

    const handleZoomIn = () => setZoom((current) => clampZoom(current + ZOOM_STEP));
    const handleZoomOut = () => setZoom((current) => clampZoom(current - ZOOM_STEP));
    const handleZoomReset = () => setZoom(1);

    const handlePreviewWheel = (event) => {
        if (isPdf || (!event.ctrlKey && !event.metaKey)) {
            return;
        }

        event.preventDefault();
        const delta = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
        setZoom((current) => clampZoom(current + delta));
    };

    const handleImageLoad = (event) => {
        syncNaturalSize(event.currentTarget);
    };

    const handlePreviewEntered = () => {
        if (isPdf) {
            return;
        }
        measureViewport();
        syncNaturalSize(imageRef.current);
    };

    useEffect(() => {
        if (!show) {
            resetPreviewState();
        }
    }, [show, item?.id]);

    useEffect(() => {
        if (!show || isPdf) {
            return undefined;
        }

        const viewport = viewportRef.current;
        if (!viewport) {
            return undefined;
        }

        measureViewport();
        syncNaturalSize(imageRef.current);

        const observer = new ResizeObserver(() => {
            measureViewport();
        });
        observer.observe(viewport);

        return () => observer.disconnect();
    }, [show, isPdf]);

    const displaySize = useMemo(() => {
        if (isPdf || !naturalSize.width || !naturalSize.height || !viewportSize.width || !viewportSize.height) {
            return null;
        }

        const fitScale = Math.min(
            viewportSize.width / naturalSize.width,
            viewportSize.height / naturalSize.height,
            1
        );

        return {
            width: Math.max(1, Math.round(naturalSize.width * fitScale * zoom)),
            height: Math.max(1, Math.round(naturalSize.height * fitScale * zoom)),
        };
    }, [isPdf, naturalSize, viewportSize, zoom]);

    const stageSize = useMemo(() => {
        if (!displaySize || !viewportSize.width || !viewportSize.height) {
            return null;
        }

        return {
            width: Math.max(displaySize.width, viewportSize.width),
            height: Math.max(displaySize.height, viewportSize.height),
        };
    }, [displaySize, viewportSize]);

    if (!item) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={handleHide}
            onEntered={handlePreviewEntered}
            size="xl"
            centered
            className="my-files-preview-modal"
            dialogClassName="my-files-preview-modal__dialog"
            contentClassName="my-files-preview-modal__content"
        >
            <Modal.Header className="my-files-preview-modal__header border-0 d-flex align-items-center">
                <div className="d-flex align-items-center gap-3 min-width-0 flex-grow-1">
                    <span className="my-files-preview-modal__icon-badge flex-shrink-0">
                        <i className={`bi ${isPdf ? 'bi-file-earmark-pdf' : 'bi-image'}`}></i>
                    </span>
                    <div className="min-width-0">
                        <Modal.Title className="h5 mb-0 fw-semibold text-truncate">{item.name}</Modal.Title>
                        <div className="small text-muted text-truncate">{item.path}</div>
                    </div>
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn ms-auto flex-shrink-0"
                    onClick={handleHide}
                    aria-label="Close"
                >
                    <i className="bi bi-x-lg"></i>
                </button>
            </Modal.Header>

            <Modal.Body className="my-files-preview-modal__body-wrap pt-2">
                {isPdf ? (
                    <>
                        <div className="my-files-preview-modal__toolbar">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                href={item.path}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="bi bi-box-arrow-up-right me-1"></i>
                                Open in new tab
                            </Button>
                        </div>
                        <div className="my-files-preview-modal__viewport my-files-preview-modal__viewport--pdf">
                            <iframe
                                title={`PDF preview: ${item.name}`}
                                src={`${item.path}#toolbar=1&navpanes=0`}
                                className="my-files-preview-modal__pdf"
                            />
                        </div>
                        <p className="my-files-preview-modal__hint mb-0">
                            Use the built-in PDF controls to scroll and zoom.
                        </p>
                    </>
                ) : (
                    <>
                        <div className="my-files-preview-modal__toolbar">
                            <ActionTooltipButton
                                tooltip="Zoom out"
                                tooltipId={`my-files-zoom-out-${item.id}`}
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleZoomOut}
                                disabled={zoom <= MIN_ZOOM}
                            >
                                <i className="bi bi-zoom-out"></i>
                            </ActionTooltipButton>
                            <button
                                type="button"
                                className="my-files-preview-modal__zoom-label"
                                onClick={handleZoomReset}
                                title="Reset zoom"
                            >
                                {Math.round(zoom * 100)}%
                            </button>
                            <ActionTooltipButton
                                tooltip="Zoom in"
                                tooltipId={`my-files-zoom-in-${item.id}`}
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleZoomIn}
                                disabled={zoom >= MAX_ZOOM}
                            >
                                <i className="bi bi-zoom-in"></i>
                            </ActionTooltipButton>
                            <ActionTooltipButton
                                tooltip="Reset zoom"
                                tooltipId={`my-files-zoom-reset-${item.id}`}
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleZoomReset}
                                disabled={zoom === 1}
                            >
                                <i className="bi bi-arrows-angle-contract"></i>
                            </ActionTooltipButton>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                href={item.path}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="bi bi-box-arrow-up-right me-1"></i>
                                Open in new tab
                            </Button>
                        </div>
                        <div
                            className="my-files-preview-modal__viewport"
                            ref={viewportRef}
                            onWheel={handlePreviewWheel}
                        >
                            <div
                                className="my-files-preview-modal__stage"
                                style={
                                    stageSize
                                        ? {
                                              width: `${stageSize.width}px`,
                                              height: `${stageSize.height}px`,
                                          }
                                        : undefined
                                }
                            >
                                <img
                                    ref={imageRef}
                                    src={item.path}
                                    alt={item.name}
                                    onLoad={handleImageLoad}
                                    draggable={false}
                                    style={
                                        displaySize
                                            ? {
                                                  width: `${displaySize.width}px`,
                                                  height: `${displaySize.height}px`,
                                              }
                                            : {
                                                  maxWidth: '100%',
                                                  maxHeight: '100%',
                                                  width: 'auto',
                                                  height: 'auto',
                                              }
                                    }
                                />
                            </div>
                        </div>
                        <p className="my-files-preview-modal__hint mb-0">
                            Use the controls to zoom, or hold Ctrl / ⌘ and scroll.
                        </p>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="my-files-preview-modal__footer border-0">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__footer-close-btn"
                    onClick={handleHide}
                >
                    <i className="bi bi-x-circle me-1"></i>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}

MediaPreviewModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    item: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        path: PropTypes.string,
        type: PropTypes.string,
    }),
};

export default MediaPreviewModal;
export { isPdfMedia };
